import { useMemo } from "react";
import {
    Box,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefusedIcon from "@mui/icons-material/Cancel";
import AcceptedIcon from "@mui/icons-material/CheckCircle";
import RecordsIcon from "@mui/icons-material/FormatListBulleted";
import SearchIcon from "@mui/icons-material/Search";
import RateIcon from "@mui/icons-material/TrendingUp";
import { Button, FoodsIcon, FoodsTags, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import {
    adminConsentRecordDecisions,
    adminConsentRecordSources,
    adminConsentRecordTypes,
    getAdminConsentRecordDecisionDescriptor,
    getAdminConsentRecordSourceDescriptor,
    getAdminConsentRecordTypeDescriptor,
} from "src/api/client-api/consent/adminConsentRecord";
import type { ConsentGovernanceRecord } from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import type {
    AdminConsentRecordDecisionFilter,
    AdminConsentRecordSourceFilter,
    AdminConsentRecordTypeFilter,
} from "../consentGovernanceTypes";
import {
    downloadCsvFile,
    formatDateTimeParts,
    formatReadableDateTimeParts,
    getConsentItemCode,
    getConsentItemName,
    getConsentVersionBadgeLabel,
    getConsentVersionTitle,
    getConsentRecordItemType,
    getUserDisplayName,
} from "../utils/consentGovernanceFormatters";
import { EllipsisFoodsTag, EllipsisTooltip, getConsentDisabledActionButtonSx } from "./ConsentAdminShared";

export type {
    AdminConsentRecordDecisionFilter,
    AdminConsentRecordSourceFilter,
    AdminConsentRecordTypeFilter,
} from "../consentGovernanceTypes";

type ConsentRecordsTabProps = {
    consentRecords: ConsentGovernanceRecord[];
    decisionFilter: AdminConsentRecordDecisionFilter;
    filteredRecords: ConsentGovernanceRecord[];
    isLoading: boolean;
    page: number;
    paginatedRecords: ConsentGovernanceRecord[];
    recordSearch: string;
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    sourceFilter: AdminConsentRecordSourceFilter;
    totalRecords: number;
    typeFilter: AdminConsentRecordTypeFilter;
    uiLocale: string;
    onDecisionFilterChange: (value: AdminConsentRecordDecisionFilter) => void;
    onPageChange: (page: number) => void;
    onRecordSearchChange: (value: string) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    onSourceFilterChange: (value: AdminConsentRecordSourceFilter) => void;
    onTypeFilterChange: (value: AdminConsentRecordTypeFilter) => void;
};

export const ConsentRecordsTab = ({
    consentRecords,
    decisionFilter,
    filteredRecords,
    isLoading,
    page,
    paginatedRecords,
    recordSearch,
    rowsPerPage,
    rowsPerPageOptions,
    sourceFilter,
    totalRecords,
    typeFilter,
    uiLocale,
    onDecisionFilterChange,
    onPageChange,
    onRecordSearchChange,
    onRowsPerPageChange,
    onSourceFilterChange,
    onTypeFilterChange,
}: ConsentRecordsTabProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const recordDecisionStats = useMemo(() => {
        let acceptedCount = 0;
        let refusedCount = 0;

        for (const record of consentRecords) {
            if (record.decision === "accepted") {
                acceptedCount += 1;
            } else if (record.decision === "refused") {
                refusedCount += 1;
            }
        }

        const totalCount = consentRecords.length;

        return {
            acceptedCount,
            percentage:
                totalCount > 0 ?
                    new Intl.NumberFormat(uiLocale, {
                        style: "percent",
                        maximumFractionDigits: 0,
                    }).format(acceptedCount / totalCount)
                :   null,
            refusedCount,
            totalCount,
        };
    }, [consentRecords, uiLocale]);
    const fieldBackgroundSx = {
        "& .MuiOutlinedInput-root": {
            bgcolor: theme.palette.common.white,
            borderRadius: theme.spacing(1.5),
        },
    };
    const tableTextSx = { ...theme.typography.body2, color: theme.custom.grey[2800] };

    const handleExportRecordsCsv = () => {
        if (filteredRecords.length === 0) {
            return;
        }

        const rows = [
            [
                t("settings.consent.records.user_col", "User"),
                t("settings.consent.records.email_col", "Email"),
                t("settings.consent.records.item_col", "Consent item"),
                t("settings.consent.item.code", "Code"),
                t("settings.consent.records.type_filter", "Type"),
                t("settings.consent.common.version", "Version"),
                t("settings.consent.version.title_col", "Version title"),
                t("settings.consent.records.status_filter", "Status"),
                t("settings.consent.common.source", "Source"),
                t("settings.consent.common.decided_at", "Decided at"),
            ],
            ...filteredRecords.map((record) => {
                const userDisplay = getUserDisplayName(record.user);
                const decisionDescriptor = getAdminConsentRecordDecisionDescriptor(record.decision);
                const sourceDescriptor = getAdminConsentRecordSourceDescriptor(record.source);
                const recordType = getConsentRecordItemType(record.consentItem);
                const recordTypeDescriptor = recordType ? getAdminConsentRecordTypeDescriptor(recordType) : null;
                const decidedAt = formatDateTimeParts(record.decidedAt, uiLocale);

                return [
                    userDisplay.name,
                    userDisplay.email,
                    getConsentItemName(record.consentItem),
                    getConsentItemCode(record.consentItem),
                    recordTypeDescriptor ? t(recordTypeDescriptor.key, recordTypeDescriptor.fallback) : "",
                    getConsentVersionBadgeLabel(record.consentItemVersion),
                    getConsentVersionTitle(record.consentItemVersion) ?? "",
                    t(decisionDescriptor.key, decisionDescriptor.fallback),
                    t(sourceDescriptor.key, sourceDescriptor.fallback),
                    decidedAt ? [decidedAt.date, decidedAt.time].filter(Boolean).join(" ") : "",
                ];
            }),
        ];

        downloadCsvFile(`consent-audit-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    };

    return (
        <Stack gap={theme.spacing(2)}>
            <Stack
                direction={{ xs: "column", xl: "row" }}
                alignItems={{ xs: "stretch", xl: "flex-start" }}
                justifyContent="space-between"
                gap={theme.spacing(2)}
            >
                <Stack gap={theme.spacing(0.75)} sx={{ minWidth: 0 }}>
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        alignItems={{ xs: "flex-start", md: "center" }}
                        gap={1.5}
                    >
                        <Typography variant="body" sx={{ fontWeight: 700 }}>
                            {t("settings.consent.records.title", "Decision audit log")}
                        </Typography>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "8px",
                                bgcolor: theme.palette.grey[100],
                                color: theme.custom.grey[2000],
                                fontSize: "12px",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                flexWrap: "wrap",
                                "& > span + span": {
                                    borderLeft: `1px solid ${theme.custom.grey[500]}`,
                                    pl: 1,
                                },
                                "& svg": {
                                    fontSize: 15,
                                },
                                maxWidth: "100%",
                            }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                <RecordsIcon sx={{ color: theme.custom.grey[2000] }} />
                                {recordDecisionStats.totalCount}{" "}
                                {t("settings.consent.records.summary_total", "records")}
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    color: theme.palette.success.main,
                                }}
                            >
                                <AcceptedIcon sx={{ color: "inherit" }} />
                                {recordDecisionStats.acceptedCount}{" "}
                                {t("settings.consent.records.accepted", "Accepted").toLowerCase()}
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    color: theme.palette.error.main,
                                }}
                            >
                                <RefusedIcon sx={{ color: "inherit" }} />
                                {recordDecisionStats.refusedCount}{" "}
                                {t("settings.consent.records.refused", "Refused").toLowerCase()}
                            </Box>
                            {recordDecisionStats.percentage && (
                                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                    <RateIcon sx={{ color: theme.custom.grey[2000] }} />
                                    {recordDecisionStats.percentage}{" "}
                                    {t("settings.consent.records.acceptance_rate", "acceptance rate")}
                                </Box>
                            )}
                        </Box>
                    </Stack>
                    <Typography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                        {t(
                            "settings.consent.records.description",
                            "This tab keeps every recorded user decision from the consent gate and the preferences center. It does not reflect the current tenant catalog configuration.",
                        )}
                    </Typography>
                </Stack>
                <Button
                    variant="black-filled"
                    size="small"
                    disabled={filteredRecords.length === 0}
                    startIcon={<FoodsIcon icon="exportDownload" size={1} />}
                    onClick={handleExportRecordsCsv}
                    sx={{
                        justifyContent: "center",
                        minHeight: 44,
                        minWidth: 150,
                        width: { xs: "100%", sm: "auto" },
                        textTransform: "none",
                        ...getConsentDisabledActionButtonSx(theme),
                    }}
                >
                    {t("settings.consent.records.export_csv", "Export CSV")}
                </Button>
            </Stack>

            <WhiteBox
                padding={theme.spacing(1.5)}
                boxShadow="none"
                sx={{
                    bgcolor: theme.palette.common.white,
                    border: `1px solid ${theme.custom.grey[500]}`,
                    borderRadius: theme.spacing(2.5),
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            xl: "minmax(420px, 1.8fr) repeat(3, minmax(180px, 1fr))",
                        },
                        gap: theme.spacing(2),
                        alignItems: "center",
                    }}
                >
                    <TextField
                        placeholder={t("settings.consent.records.search_placeholder", "Search a user or item...")}
                        size="small"
                        sx={{
                            minWidth: 0,
                            ...fieldBackgroundSx,
                        }}
                        value={recordSearch}
                        onChange={(event) => onRecordSearchChange(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        sx={{
                                            color: theme.custom.grey[2000],
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        select
                        label={t("settings.consent.records.status_filter", "Status")}
                        value={decisionFilter}
                        onChange={(event) =>
                            onDecisionFilterChange(event.target.value as AdminConsentRecordDecisionFilter)
                        }
                        size="small"
                        sx={fieldBackgroundSx}
                    >
                        <MenuItem value="all">{t("settings.consent.records.all_decisions", "All statuses")}</MenuItem>
                        {adminConsentRecordDecisions.map((decision) => {
                            const descriptor = getAdminConsentRecordDecisionDescriptor(decision);

                            return (
                                <MenuItem key={decision} value={decision}>
                                    {t(descriptor.key, descriptor.fallback)}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    <TextField
                        select
                        label={t("settings.consent.common.source", "Source")}
                        value={sourceFilter}
                        onChange={(event) => onSourceFilterChange(event.target.value as AdminConsentRecordSourceFilter)}
                        size="small"
                        sx={fieldBackgroundSx}
                    >
                        <MenuItem value="all">{t("settings.consent.records.all_sources", "All sources")}</MenuItem>
                        {adminConsentRecordSources.map((source) => {
                            const descriptor = getAdminConsentRecordSourceDescriptor(source);

                            return (
                                <MenuItem key={source} value={source}>
                                    {t(descriptor.key, descriptor.fallback)}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    <TextField
                        select
                        label={t("settings.consent.records.type_filter", "Type")}
                        value={typeFilter}
                        onChange={(event) => onTypeFilterChange(event.target.value as AdminConsentRecordTypeFilter)}
                        size="small"
                        sx={fieldBackgroundSx}
                    >
                        <MenuItem value="all">{t("settings.consent.records.all_types", "All types")}</MenuItem>
                        {adminConsentRecordTypes.map((type) => {
                            const descriptor = getAdminConsentRecordTypeDescriptor(type);

                            return (
                                <MenuItem key={type} value={type}>
                                    {t(descriptor.key, descriptor.fallback)}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </Box>
            </WhiteBox>

            {isLoading ?
                <Stack minHeight="200px" alignItems="center" justifyContent="center">
                    <CircularProgress />
                </Stack>
            : filteredRecords.length === 0 ?
                <WhiteBox padding={theme.spacing(4)} boxShadow={theme.custom.shadows.grey}>
                    <Typography variant="body" sx={{ color: theme.custom.grey[2000], textAlign: "center" }}>
                        {t("settings.consent.records.empty.title", "No record matches these filters")}
                    </Typography>
                </WhiteBox>
            :   <WhiteBox
                    padding={0}
                    boxShadow={theme.custom.shadows.grey}
                    sx={{ maxWidth: "100%", overflow: "hidden" }}
                >
                    <Box sx={{ maxWidth: "100%", overflowX: { xs: "auto", lg: "hidden" } }}>
                        <Table size="small" sx={{ width: "100%", minWidth: { xs: 1200, lg: 0 }, tableLayout: "fixed" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 220, whiteSpace: "nowrap" }}>
                                        {t("settings.consent.records.user_col", "User")}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        {t("settings.consent.records.item_col", "Consent item")}
                                    </TableCell>
                                    <TableCell sx={{ width: 140, whiteSpace: "nowrap" }}>
                                        {t("settings.consent.records.type_filter", "Type")}
                                    </TableCell>
                                    <TableCell sx={{ width: 220, whiteSpace: "nowrap" }}>
                                        {t("settings.consent.common.version", "Version")}
                                    </TableCell>
                                    <TableCell sx={{ width: 160, whiteSpace: "nowrap" }}>
                                        {t("settings.consent.common.decision", "Decision")}
                                    </TableCell>
                                    <TableCell sx={{ width: 220, whiteSpace: "nowrap" }}>
                                        {t("settings.consent.common.source", "Source")}
                                    </TableCell>
                                    <TableCell sx={{ width: 160, whiteSpace: "nowrap" }}>
                                        {t("settings.consent.common.decided_at", "Decided at")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRecords.map((record) => {
                                    const userDisplay = getUserDisplayName(record.user);
                                    const decisionDescriptor = getAdminConsentRecordDecisionDescriptor(record.decision);
                                    const sourceDescriptor = getAdminConsentRecordSourceDescriptor(record.source);
                                    const recordType = getConsentRecordItemType(record.consentItem);
                                    const recordTypeDescriptor =
                                        recordType ? getAdminConsentRecordTypeDescriptor(recordType) : null;
                                    const decidedAtDisplay = formatReadableDateTimeParts(record.decidedAt, uiLocale);
                                    const itemName = getConsentItemName(record.consentItem);
                                    const versionBadgeLabel = getConsentVersionBadgeLabel(record.consentItemVersion);
                                    const versionTitle = getConsentVersionTitle(record.consentItemVersion);
                                    const versionTagLabel =
                                        versionTitle ? `${versionBadgeLabel} · ${versionTitle}` : versionBadgeLabel;
                                    const isConsentGateSource = record.source === "consent_gate";

                                    return (
                                        <TableRow key={record.id}>
                                            <TableCell>
                                                <Stack gap="1px">
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {userDisplay.name}
                                                    </Typography>
                                                    {userDisplay.email && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.custom.grey[2000],
                                                                fontSize: "11px",
                                                            }}
                                                        >
                                                            {userDisplay.email}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <EllipsisTooltip
                                                    title={itemName}
                                                    sx={{ ...tableTextSx, fontWeight: 700 }}
                                                >
                                                    {itemName}
                                                </EllipsisTooltip>
                                            </TableCell>
                                            <TableCell>
                                                {recordTypeDescriptor ?
                                                    <FoodsTags
                                                        label={t(
                                                            recordTypeDescriptor.key,
                                                            recordTypeDescriptor.fallback,
                                                        )}
                                                        variant={recordTypeDescriptor.variant}
                                                    />
                                                :   <Typography variant="body2">—</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 0 }}>
                                                <EllipsisFoodsTag label={versionTagLabel} variant="tags-grey" />
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" gap="6px">
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: "50%",
                                                            bgcolor:
                                                                record.decision === "accepted" ?
                                                                    theme.palette.success.main
                                                                : record.decision === "refused" ?
                                                                    theme.palette.error.main
                                                                :   theme.custom.grey[2000],
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <FoodsTags
                                                        label={t(decisionDescriptor.key, decisionDescriptor.fallback)}
                                                        variant={decisionDescriptor.variant}
                                                    />
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 0 }}>
                                                <EllipsisFoodsTag
                                                    label={t(sourceDescriptor.key, sourceDescriptor.fallback)}
                                                    startIcon={isConsentGateSource ? "webhook" : "settings"}
                                                    variant={isConsentGateSource ? "tags-blue" : "tags-coral"}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {decidedAtDisplay ?
                                                    <Stack gap="1px">
                                                        <Typography variant="body2">{decidedAtDisplay.date}</Typography>
                                                        {decidedAtDisplay.time && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: theme.custom.grey[2000],
                                                                    fontSize: "11px",
                                                                }}
                                                            >
                                                                {decidedAtDisplay.time}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                :   <Typography variant="body2">—</Typography>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                    <TablePagination
                        component="div"
                        count={totalRecords}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={rowsPerPageOptions}
                        onPageChange={(_, nextPage) => onPageChange(nextPage)}
                        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
                    />
                </WhiteBox>
            }
        </Stack>
    );
};
